import { useState } from "react";
import {
  feedbackBatchesCreateFeedbackBatch,
  feedbackBatchesAddEvaluationsToBatch,
} from "../../gen";
import type { EvaluationResponse, BatchResponse } from "../../gen/models";

interface UseThreadBatchModalsOptions {
  evaluations: EvaluationResponse[];
  activeBatches?: BatchResponse[];
  activeBatchId: string;
  setActiveBatchId: (id: string) => void;
  setActiveBatches: React.Dispatch<React.SetStateAction<BatchResponse[]>>;
  setAllBatches: React.Dispatch<React.SetStateAction<BatchResponse[]>>;
  loadThreadData: (isBackground?: boolean) => Promise<void>;
  showToast: (message: string, options?: { type?: "info" | "success" | "warning" | "error" }) => void;
}

export function useThreadBatchModals({
  evaluations,
  activeBatches = [],
  activeBatchId,
  setActiveBatchId,
  setActiveBatches,
  setAllBatches,
  loadThreadData,
  showToast,
}: UseThreadBatchModalsOptions) {
  const [isBatchAssignModalOpen, setIsBatchAssignModalOpen] = useState(false);
  const [modalEvaluations, setModalEvaluations] = useState<EvaluationResponse[] | null>(null);
  const [isActiveBatchModalOpen, setIsActiveBatchModalOpen] = useState(false);
  const [pendingBatchAction, setPendingBatchAction] = useState<((batchId: string) => void) | null>(null);
  const [targetBatchId, setTargetBatchId] = useState("");
  const [newBatchTitle, setNewBatchTitle] = useState("");
  const [creatingBatchInline, setCreatingBatchInline] = useState(false);
  const [assigningEvaluations, setAssigningEvaluations] = useState(false);
  const [assignAllEvaluationsInline, setAssignAllEvaluationsInline] = useState(true);

  const effectiveEvaluations = modalEvaluations ?? evaluations;

  const openBatchAssignModal = (evals?: EvaluationResponse[]) => {
    setModalEvaluations(evals || null);
    setTargetBatchId(activeBatchId || (activeBatches[0]?.id ?? ""));
    setCreatingBatchInline(false);
    setNewBatchTitle("");
    setAssignAllEvaluationsInline(true);
    setIsBatchAssignModalOpen(true);
  };

  const handleCloseBatchAssignModal = () => {
    setIsBatchAssignModalOpen(false);
    setModalEvaluations(null);
  };

  // Request active batch selection/creation before note or comment
  const ensureActiveBatch = (onReady?: (batchId: string) => void) => {
    if (activeBatchId) {
      if (onReady) onReady(activeBatchId);
    } else {
      if (onReady) {
        setPendingBatchAction(() => onReady);
      }
      setIsActiveBatchModalOpen(true);
    }
  };

  // Direct one-click assignment to currently active batch
  const handleAssignDirectToActiveBatch = async (evalIds: string[]) => {
    if (!activeBatchId) {
      showToast("No active batch selected.", { type: "warning" });
      return;
    }
    if (evalIds.length === 0) {
      showToast("No evaluations to assign.", { type: "info" });
      return;
    }
    setAssigningEvaluations(true);
    try {
      await feedbackBatchesAddEvaluationsToBatch(activeBatchId, {
        evaluation_ids: evalIds,
      });
      showToast(
        evalIds.length === 1
          ? "Successfully added evaluation to batch."
          : `Successfully added ${evalIds.length} evaluations to batch.`,
        { type: "success" }
      );
      await loadThreadData(true);
    } catch (err: any) {
      showToast("Failed to assign evaluations to batch: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setAssigningEvaluations(false);
    }
  };

  // Assign evaluations on thread to target batch
  const handleAssignEvaluationsToBatch = async () => {
    let batchId = targetBatchId;
    if (creatingBatchInline && newBatchTitle.trim()) {
      try {
        const createRes = await feedbackBatchesCreateFeedbackBatch({
          title: newBatchTitle.trim(),
        });
        batchId = (createRes as any).data.id;
        setActiveBatchId(batchId);
      } catch (err: any) {
        showToast("Failed to create batch: " + (err?.message || "Unknown error"), { type: "error" });
        return;
      }
    }

    if (!batchId) {
      showToast("Please select or create a feedback batch.", { type: "warning" });
      return;
    }

    const evalIds = effectiveEvaluations.map((e) => e.id);
    if (creatingBatchInline && !assignAllEvaluationsInline) {
      showToast("Created new batch: " + newBatchTitle.trim(), { type: "success" });
      setIsBatchAssignModalOpen(false);
      setModalEvaluations(null);
      await loadThreadData(true);
      return;
    }

    if (evalIds.length === 0) {
      showToast("No evaluation records available on this thread to assign.", { type: "info" });
      return;
    }

    setAssigningEvaluations(true);
    try {
      await feedbackBatchesAddEvaluationsToBatch(batchId, {
        evaluation_ids: evalIds,
      });
      showToast(
        evalIds.length === 1
          ? "Successfully added evaluation to batch."
          : `Successfully added ${evalIds.length} evaluations to batch.`,
        { type: "success" }
      );
      setIsBatchAssignModalOpen(false);
      setModalEvaluations(null);
      await loadThreadData(true);
    } catch (err: any) {
      showToast("Failed to assign evaluations to batch: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setAssigningEvaluations(false);
    }
  };

  // Create batch from the ActiveBatchSelectModal prompt
  const handleCreateActiveBatch = async (
    title: string,
    description?: string,
    assignAllEvaluations?: boolean
  ): Promise<string | null> => {
    try {
      const res = await feedbackBatchesCreateFeedbackBatch({
        title,
        description,
      });
      const newBatch = (res as any).data as BatchResponse;
      setAllBatches((prev) => [newBatch, ...prev]);
      setActiveBatches((prev) => [newBatch, ...prev]);
      setActiveBatchId(newBatch.id);
      setTargetBatchId(newBatch.id);

      if (assignAllEvaluations && evaluations.length > 0) {
        try {
          await feedbackBatchesAddEvaluationsToBatch(newBatch.id, {
            evaluation_ids: evaluations.map((e) => e.id),
          });
          showToast(
            `Created batch "${newBatch.title}" and assigned ${evaluations.length} evaluation(s).`,
            { type: "success" }
          );
        } catch (assignErr: any) {
          console.error("Failed to assign evaluations to new batch:", assignErr);
          showToast("Created new active batch: " + newBatch.title, { type: "success" });
        }
      } else {
        showToast("Created new active batch: " + newBatch.title, { type: "success" });
      }

      if (pendingBatchAction) {
        const cb = pendingBatchAction;
        setPendingBatchAction(null);
        cb(newBatch.id);
      }

      return newBatch.id;
    } catch (err: any) {
      showToast("Failed to create batch: " + (err?.message || "Unknown error"), { type: "error" });
      return null;
    }
  };

  const handleSelectActiveBatch = (batchId: string) => {
    setActiveBatchId(batchId);
    setTargetBatchId(batchId);
    if (pendingBatchAction) {
      const cb = pendingBatchAction;
      setPendingBatchAction(null);
      cb(batchId);
    }
  };

  const handleCloseActiveBatchModal = () => {
    setIsActiveBatchModalOpen(false);
    setPendingBatchAction(null);
  };

  return {
    isBatchAssignModalOpen,
    setIsBatchAssignModalOpen,
    openBatchAssignModal,
    handleCloseBatchAssignModal,
    effectiveEvaluations,
    handleAssignDirectToActiveBatch,
    isActiveBatchModalOpen,
    setIsActiveBatchModalOpen,
    targetBatchId,
    setTargetBatchId,
    newBatchTitle,
    setNewBatchTitle,
    creatingBatchInline,
    setCreatingBatchInline,
    assigningEvaluations,
    assignAllEvaluationsInline,
    setAssignAllEvaluationsInline,
    ensureActiveBatch,
    handleAssignEvaluationsToBatch,
    handleCreateActiveBatch,
    handleSelectActiveBatch,
    handleCloseActiveBatchModal,
  };
}
